import React from "react";
import { Info, X } from "lucide-react";
import { Button } from "@mui/material";

import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { DEFAULT_ADDITIONAL_PROMPT } from "@/constants/copilot";
import { Button as ShButton } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

interface SystemPromptProps {
  isOpen: boolean;
  additionalPrompt: string;
  onClose: () => void;
  onConfirm: () => void;
  setAdditionalPrompt: (additionalPrompt: string) => void;
  systemPrompt: string;
  setSystemPrompt: (systemPrompt: string) => void;
  editedSystemPrompt: string;
  setEditedSystemPrompt: (systemPrompt: string) => void;
  handleSystemPromptUpdate: (systemPrompt: string) => void;
}

const SystemPrompt: React.FC<SystemPromptProps> = ({
  isOpen,
  onClose,
  onConfirm,
  additionalPrompt,
  setAdditionalPrompt,
  systemPrompt,
  setSystemPrompt,
  editedSystemPrompt,
  setEditedSystemPrompt,
  handleSystemPromptUpdate,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md md:max-w-lg lg:max-w-xl p-0 overflow-hidden border border-gray-200 rounded-md">
        <div className="absolute right-2 top-2 z-10">
          <ShButton
            variant="ghost"
            size="icon"
            onClick={onClose}
            className={`h-8 w-8 rounded-sm hover:bg-brand-secondary`}
          >
            <X className="h-4 w-4 text-white" />
          </ShButton>
        </div>

        <div className="bg-brand-primary py-4 px-6 w-full flex justify-center" style={{ alignItems: "center" }}>
          <h2 className="text-xl font-semibold text-center text-white">
            System prompt
          </h2>
          <HoverCard>
            <HoverCardTrigger asChild>
              <Info className="ml-2 text-white" />
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
              <div className="flex justify-between space-x-4">
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold">Info</h4>
                  <p className="text-sm">
                    Use System Prompt to set the overall behavior for the model. Example: "You are a friendly and helpful assistant.
                    Ensure your answers are complete, unless the user
                    requests a more concise approach."
                  </p>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        </div>

        <ScrollArea className="p-6 max-h-[60vh]">
          <Textarea
            placeholder="You are a helpful and informative AI assistant..."
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
                        onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                e.stopPropagation();
                onConfirm();
              }
              if (e.key === "Backspace") {
                e.stopPropagation();
              }
            }}
            className="mt-2  min-h-[40vh]"
          />
        </ScrollArea>

        <div className="text-sm ml-6">
          Use <span className="bg-muted px-1 rounded-sm">shift + enter</span>{" "}
          for new line
        </div>

        <DialogFooter className="flex justify-end gap-2 p-4 border-t">
          <Button
            sx={{
              borderRadius: "2rem",
              border: "0.125rem solid var(--colors-brand-secondary)",
              ":hover": {
                color: "var(--colors-brand-tertiary)",
                border: "0.125rem solid var(--colors-brand-tertiary)",
              },
            }}
            onClick={() => setSystemPrompt(DEFAULT_ADDITIONAL_PROMPT)}
            disabled={systemPrompt === DEFAULT_ADDITIONAL_PROMPT}
          >
            Reset Prompt
          </Button>
          <Button
            sx={{
              borderRadius: "2rem",
              border: "0.125rem solid var(--colors-brand-secondary)",
              ":hover": {
                color: "var(--colors-brand-tertiary)",
                border: "0.125rem solid var(--colors-brand-tertiary)",
              },
            }}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ borderRadius: "2rem" }}
            onClick={onConfirm}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SystemPrompt;
